import { z } from 'zod';
/**
 * **Der Vertrag des statischen Vorlagenprüfers** — Auftrag `C-mailkatalog`
 * (2026-09-02), Task 2 des Plans
 * `docs/superpowers/plans/2026-09-02-email-vorlagen-umsetzung.md`, Spec
 * `docs/superpowers/specs/2026-09-02-email-vorlagen-design.md` Abschnitt 6.
 *
 * Diese Datei trägt **nur** das Ergebnis der Prüfung, nicht die Ablage
 * (`mailvorlage`, `mailvorlage_fassung`) und nicht die Endpunkte — die
 * entstehen in `C-mailcrud` und legen vermutlich `mailvorlagen.ts` daneben an.
 * Der Dateiname ist bewusst nicht `mailvorlagen.ts`: Der Umsetzungsplan nennt
 * genau diesen Namen als Datei von `C-mailcrud` (Task 3), das an
 * `C-mailkatalog` **hängt** und erst nach dessen Landung entsteht. Zwei
 * Aufträge, die dieselbe neue Datei anlegen, wären ein Konflikt und kein
 * Parallelismus (Plan, Abschnitt „Warum … und warum parallel").
 *
 * **Warum ein eigener Vertrag und nicht nur ein Rückgabetyp in
 * `apps/api`.** Der Prüfer wird über einen Endpunkt erreichbar sein
 * (`POST /api/v1/mailvorlagen/vorschau` meldet unter anderem, ob der Text
 * gültig ist, und die Oberfläche `F-mailmaske` zeigt die Befunde an). Ein
 * Ergebnis, das über die Grenze zwischen API und Oberfläche geht, ist ein
 * Vertrag wie jeder andere — geprüft auf beiden Seiten mit demselben Schema.
 */
/**
 * Die fünf Layoutschlüssel aus `TEMPLATE_BY_TYPE`
 * (`apps/api/src/render/template-registry.ts`) — hier als eigenes Zod-Enum,
 * weil Verträge kein Modul aus `apps/api` importieren (die Verträge sind das
 * gemeinsame Fundament, `apps/api` baut darauf auf und nicht umgekehrt).
 *
 * **Die Deckungsgleichheit mit `templateKeys()` ist gemessen und nicht
 * behauptet:** Fall `E-706` (Migration `0130`, Auftrag `M-mailvorlage`) hält
 * die fünf Werte des `CHECK` in der Datenbank gegen `templateKeys()` —
 * dieselbe Bauart wie `template-registry.test.ts` gegen `pg_enum`. Diese
 * Datei ist die **dritte** Stelle, an der die fünf Namen stehen, und trägt
 * deshalb selbst keinen eigenen Fall: Sie ist ein Vertrag über Text, den
 * TypeScript bereits bei einer Abweichung als Typfehler meldet, sobald
 * `MailAnlass` neben `TemplateKey` verwendet wird.
 */
export const mailAnlassSchema = z.enum([
    'invoice',
    'credit-note',
    'cancellation',
    'quote',
    'delivery-note',
]);
/** `rahmen` — das eine Standard-Design; `inhalt` — eine Vorlage je Anlass und Sprache. */
export const mailArtSchema = z.enum(['rahmen', 'inhalt']);
/**
 * Welches Feld einer Fassung geprüft wird.
 *
 * Alle drei laufen durch dieselbe Vorlagensprache und denselben Katalog
 * (`D-15`, `E-730`); `koerper_text` bekommt zusätzlich die Markup-Ablehnung
 * für **jedes** Markup und nicht nur die Ablehnungsliste aus `D-13`, weil er
 * die Textalternative ist und kein HTML sein darf.
 */
export const mailPruefungFeldSchema = z.enum(['betreff', 'koerper_html', 'koerper_text']);
/**
 * Die Fehlercodes des Prüfers, einer je benannter Zusicherung aus Spec 6/7.
 *
 * **Warum benannte Codes und nicht nur ein Meldungstext** — dieselbe
 * Begründung wie bei `DATENREGEL_FEHLER` (`datenregeln.ts`): Eine Oberfläche,
 * die auf einen Fehlercode reagiert (zum Beispiel: das Feld markieren, das den
 * Platzhalter trägt), bricht nicht bei der nächsten Umformulierung der
 * deutschen Meldung.
 */
export const mailPruefungFehlercodeSchema = z.enum([
    /** Ein `{{pfad}}`, der in KEINEM Katalog vorkommt — mutmasslich ein Tippfehler. `E-713` */
    'MAILVORLAGE_PLATZHALTER_UNBEKANNT',
    /** Ein `{{> teil}}` — Mailvorlagen kennen keine eingebundenen Teile. `E-714` */
    'MAILVORLAGE_TEIL_UNBEKANNT',
    /** Ein `{{#if}}`/`{{#each}}` ohne passendes Ende, in falscher Reihenfolge oder umgekehrt. `E-715` */
    'MAILVORLAGE_BLOCK_UNBALANCIERT',
    /** `<script>`, `<iframe>`, `on…=`, `javascript:` und Geschwister aus der Ablehnungsliste (`D-13`). `E-716` */
    'MAILVORLAGE_MARKUP_VERBOTEN',
    /** Ein Zeilenumbruch im Betreff (Header-Injection). `E-717` */
    'MAILVORLAGE_BETREFF_ZEILENUMBRUCH',
    /** Eine Rahmenfassung trägt `{{inhalt}}` nicht genau einmal. `E-718` */
    'MAILVORLAGE_INHALT_PLATZHALTER_ANZAHL',
    /** Eine Inhaltsfassung verwendet `{{inhalt}}` — das ist dem Rahmen vorbehalten. `E-719` */
    'MAILVORLAGE_INHALT_PLATZHALTER_IM_INHALT',
    /** Ein Rahmen verwendet einen Pfad, der nur Inhaltsvorlagen zusteht (`beleg.*`, `kunde.*`, …). `E-720` */
    'MAILVORLAGE_BELEGPFAD_IM_RAHMEN',
    /** `koerper_text` enthält ein HTML-Element — die Textalternative darf keines sein (`D-15`). `E-730` */
    'MAILVORLAGE_MARKUP_IM_TEXTKOERPER',
]);
/** Die Fehlercodes als benannte Konstanten, wie `DATENREGEL_FEHLER`. */
export const MAIL_PRUEFUNG_FEHLER = mailPruefungFehlercodeSchema.enum;
/** Ein einzelner Befund: sein Code, der betroffene Platzhalterpfad (falls vorhanden), und eine lesbare Meldung. */
export const mailPruefungBefundSchema = z.object({
    code: mailPruefungFehlercodeSchema,
    pfad: z.string().optional(),
    meldung: z.string(),
});
/**
 * Das Ergebnis einer Prüfung: `ok` ist genau dann `true`, wenn `befunde` leer
 * ist — kein Aufrufer soll beide getrennt auswerten müssen und dabei
 * auseinanderlaufen können.
 */
export const mailPruefungErgebnisSchema = z.object({
    ok: z.boolean(),
    befunde: z.array(mailPruefungBefundSchema),
});
/** Ein Eintrag im Platzhalterkatalog: der Pfad und seine Herkunft im Ansichtsmodell (`D-6`). */
export const mailKatalogEintragSchema = z.object({
    pfad: z.string(),
    herkunft: z.string(),
});
//# sourceMappingURL=mailvorlagen-pruefung.js.map